import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  MissRateChart, AvgTimeChart, CategoryWeaknessChart, SchoolCompareChart, GrowthChart,
  type MissRateRow, type AvgTimeRow, type CategoryWeaknessRow, type SchoolCompareRow, type GrowthRow,
} from "@/components/AnalyticsCharts";

interface AnalyticsData {
  miss: MissRateRow[];
  avgTime: AvgTimeRow[];
  weakness: CategoryWeaknessRow[];
  schoolCompare: SchoolCompareRow[];
  growth: GrowthRow[];
}

export default function AdminAnalyticsPage() {
  const [d, setD] = useState<AnalyticsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        // Pull a window of recent answers; aggregate client-side. For larger
        // installs, swap this for a Postgres view or RPC.
        const { data: answers } = await supabase
          .from("student_answers")
          .select("question_id, is_correct, time_seconds")
          .limit(5000);
        const { data: questions } = await supabase
          .from("questions")
          .select("id, question_text, category");
        const qMap = new Map<string, { question_text: string; category: string | null }>();
        (questions ?? []).forEach((q) => qMap.set(q.id, q));

        const buckets = new Map<string, { tot: number; missed: number; time: number }>();
        const catBuckets = new Map<string, number>();
        (answers ?? []).forEach((a) => {
          const q = qMap.get(a.question_id as string);
          if (!q) return;
          const key = q.question_text.slice(0, 40);
          const b = buckets.get(key) ?? { tot: 0, missed: 0, time: 0 };
          b.tot += 1;
          if (a.is_correct === false) b.missed += 1;
          b.time += Number(a.time_seconds ?? 0);
          buckets.set(key, b);
          if (a.is_correct === false && q.category) {
            catBuckets.set(q.category, (catBuckets.get(q.category) ?? 0) + 1);
          }
        });
        const miss: MissRateRow[] = Array.from(buckets.entries())
          .map(([label, b]) => ({ label, miss_rate_pct: Math.round((b.missed / b.tot) * 100) }))
          .sort((a, b) => b.miss_rate_pct - a.miss_rate_pct).slice(0, 12);
        const avgTime: AvgTimeRow[] = Array.from(buckets.entries())
          .map(([label, b]) => ({ label, avg_time_sec: Math.round(b.time / b.tot) }))
          .sort((a, b) => b.avg_time_sec - a.avg_time_sec).slice(0, 12);
        const weakness: CategoryWeaknessRow[] = Array.from(catBuckets.entries())
          .map(([category, missed]) => ({ category, missed }))
          .sort((a, b) => b.missed - a.missed).slice(0, 8);

        // School comparison from leaderboard view
        const { data: lb } = await supabase
          .from("leaderboard_view")
          .select("school_name, best_score").not("school_name", "is", null);
        const schoolBuckets = new Map<string, { sum: number; n: number }>();
        (lb ?? []).forEach((r) => {
          const k = r.school_name as string;
          const prev = schoolBuckets.get(k) ?? { sum: 0, n: 0 };
          schoolBuckets.set(k, { sum: prev.sum + Number(r.best_score), n: prev.n + 1 });
        });
        const schoolCompare: SchoolCompareRow[] = Array.from(schoolBuckets.entries())
          .map(([school, v]) => ({ school, avg_score: Math.round(v.sum / v.n) }))
          .sort((a, b) => b.avg_score - a.avg_score).slice(0, 10);

        // Growth: synthesized from a single student's last attempts (placeholder).
        // For a real view, write a SQL view that orders sessions by completed_at per student.
        const { data: sessions } = await supabase
          .from("game_sessions")
          .select("student_id, score, completed_at")
          .eq("status", "completed")
          .order("completed_at", { ascending: true })
          .limit(200);
        const growth: GrowthRow[] = (sessions ?? []).slice(-12).map((g, i) => ({ attempt: i + 1, score: Number(g.score) }));

        setD({ miss, avgTime, weakness, schoolCompare, growth });
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load analytics.");
      }
    }
    void load();
  }, []);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl text-white">Analytics</h1>
        <p className="text-steel-300">Question miss rate, time per question, category weaknesses, school comparison, growth.</p>
      </header>

      {error && <div className="pill-bad px-3 py-2">{error}</div>}
      {!d && !error && (
        <div className="text-steel-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Computing…</div>
      )}

      {d && (
        <>
          <div className="grid lg:grid-cols-2 gap-4">
            <MissRateChart rows={d.miss} />
            <AvgTimeChart rows={d.avgTime} />
            <CategoryWeaknessChart rows={d.weakness} />
            <SchoolCompareChart rows={d.schoolCompare} />
          </div>
          <GrowthChart rows={d.growth} />
        </>
      )}
    </div>
  );
}
